<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDebounceFn } from '@vueuse/core';
import { STATUS_OPEN, STATUS_CLOSED } from '@/constants';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { useAirManagementStore } from '@/store/AirManagementStore';
import { usePackageManagementStore } from '@/store/PackageManagementStore';
import { useViewComposable } from '@/views/composable';
import { useDialog } from 'primevue/usedialog';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import ProgressSpinner from 'primevue/progressspinner';
import Select from 'primevue/select';
import TableColumn from 'primevue/column';
import Tag from 'primevue/tag';
import ToggleSwitch from 'primevue/toggleswitch';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import ExportExcelFile from '@/components/modal/utils/ExportExcelFile.vue';
import ImportFileModal from '@/components/modal/ImportFileModal.vue';
import UpdateAirModal from './UpdateAirModal.vue';
import ProgressModal from '@/components/modal/ProgressModal.vue';
import ViewAirDataModal from '@/components/modal/ViewAirDataModal.vue';
import ViewVersionRevertModal from '@/components/modal/ViewVersionRevertModal.vue';

const dialog = useDialog();
const toast = useToast();
const { t } = useI18n({ useScope: 'global' });

const filters = ref();
const loading = ref(false);
const isLoadingImport = ref(false);
const isShowModal = ref(false);
const isSuccess = ref(false);
const isRevert = ref(false);
const isLoadingPage = ref(false);
const globalFilterValue = ref();
const statuses = ref([STATUS_OPEN, STATUS_CLOSED]);
const sheetData = ref<any>([]);
const widthList = [10, 40, 40, 20, 20, 20, 20, 30, 30, 15, 15, 20, 20, 20];

const airManagementStore = useAirManagementStore();
const packageManagementStore = usePackageManagementStore();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const { airFiltersInit, styleColumnExport, renderNameFileExport } = useViewComposable();
const sheetColumnStyle = styleColumnExport(widthList);
const airs = computed(() => airManagementStore.airs);

const initAirFilters = () => {
  filters.value = airFiltersInit(globalFilterValue.value);
};
initAirFilters();

const formatDataExport = (listData: any) => {
  return listData?.map((item: any) => {
    const airData: any = {};
    airData[t('management.air.airport_code')] = item.airportCode;
    airData[`${t('management.air.airport_name')} (EN)`] = item.airportName;
    airData[`${t('management.air.airport_name')} (FR)`] = item.airportNameFR;
    airData[`${t('management.air.city_name')} (EN)`] = item.cityName;
    airData[`${t('management.air.city_name')} (FR)`] = item.cityNameFr;
    airData[`${t('management.air.region')} (EN)`] = item.region;
    airData[`${t('management.air.region')} (FR)`] = item.regionFr;
    airData[`${t('management.air.country_name')} (EN)`] = item.countryName;
    airData[`${t('management.air.country_name')} (FR)`] = item.countryNameFr;
    airData[t('management.air.destination_status')] = item.destSts;
    airData[t('management.air.gateway_status')] = item.gateSts;
    airData[t('management.air.dest_sv_id')] = formatDestSvId(item.destSVId);
    return airData;
  });
};
const formatDestSvId = (destSVId: string) => (destSVId ? destSVId.replace(/,/g, '-') : '');

const clearFilter = () => {
  globalFilterValue.value = null;
  initAirFilters();
};

const getSeverity = (status: string) => {
  switch (status) {
    case STATUS_OPEN:
      return 'info';
    case STATUS_CLOSED:
      return 'danger';
  }
};

const onUpdateStatus = async (data: any, type: string) => {
  try {
    data.type = type;
    await airManagementStore.updateAirStatus(data);
    toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
  } catch {
    toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
  }
};

const handleUploadData = async () => {
  isLoadingPage.value = true;
  airManagementStore
    .getListAir()
    .then(() => {
      packageManagementStore.getListDestination();
      sheetData.value = [
        {
          name: 'Sheet 1',
          data: formatDataExport(airs.value)
        }
      ];
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => {
      isLoadingPage.value = false;
    });
};

const editAirport = (data: any, header: any) => {
  dialog.open(UpdateAirModal, {
    data: {
      data: data
    },
    props: {
      header,
      modal: true
    },
    emits: {
      updateData: () => {
        handleUploadData();
      }
    }
  });
};

const deleteAirport = async (data: any) => {
  isLoadingPage.value = true;
  airManagementStore
    .deleteAirport(data.sk)
    .then(() => {
      toast.add({ severity: 'success', summary: '', detail: t('notification.delete_successfully'), life: 3000 });
      handleUploadData();
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.delete_error'), life: 5000 });
    })
    .finally(() => {
      isLoadingPage.value = false;
    });
};

const showModalImportExcel = () => {
  const dialogRef = dialog.open(ImportFileModal, {
    props: {
      header: t('common.labels.upload_air'),
      modal: true
    },
    data: {
      url: airManagementStore.getUrlUploadFileAir
    },
    emits: {
      onApply: (data: any, file: any) => {
        handleApply(data, file, 'IMPORT');
        dialogRef.close();
      },
      onClose: () => {
        dialogRef.close();
      }
    }
  });
};

const handleImportFileAir = async (file: any) => {
  isLoadingImport.value = true;
  isShowModal.value = true;
  isRevert.value = false;
  const payload = new FormData();
  payload.append('file', file);
  airManagementStore
    .importFileAir(payload)
    .then(() => {
      isSuccess.value = true;
      handleUploadData();
    })
    .catch((error) => {
      isSuccess.value = false;
      if (error.response && error.response.status === 304) {
        toast.add({ severity: 'error', summary: '', detail: t('notification.airport_has_been_mapped') });
      }
    })
    .finally(() => {
      isLoadingImport.value = false;
    });
};

const showRevertVersion = async (type: string) => {
  packageManagementStore
    .getVersionRevert(type)
    .then((versions) => {
      handleOpenModalRevertAir(versions?.data);
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    });
};

const handleOpenModalRevertAir = (versions: any) => {
  const dialogRef = dialog.open(ViewVersionRevertModal, {
    props: {
      header: t('common.buttons.revert'),
      style: {
        width: '95vw'
      },
      modal: true,
      maximizable: true
    },
    data: {
      versions
    },
    emits: {
      onPreview: (fileName: string) => {
        handleGetPreviewVersionAir(fileName, 'PREVIEW');
      },
      onRevert: (fileName: string) => {
        handleGetPreviewVersionAir(fileName, 'REVERT');
        dialogRef.close();
      },
      onExport: (fileName: string) => {
        handleGetPreviewVersionAir(fileName, 'EXPORT');
      },
      onClose: () => {
        dialogRef.close();
      }
    }
  });
};

const handleGetPreviewVersionAir = async (fileName: string, type: string) => {
  isLoadingPage.value = true;
  airManagementStore
    .getPreviewAirByFileName(fileName)
    .then((data) => {
      handleApply(data?.data, fileName, type);
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => {
      isLoadingPage.value = false;
    });
};

const handleApply = (data: any, file?: any, type?: string) => {
  const dialogRef = dialog.open(ViewAirDataModal, {
    props: {
      header: t('common.labels.review_upload_file'),
      style: {
        width: '95vw'
      },
      modal: true,
      maximizable: true
    },
    data: {
      airData: data,
      type,
      sheetData: formatDataExport(data)
    },
    emits: {
      onApply: () => {
        if (type === 'IMPORT') {
          handleImportFileAir(file);
        } else if (type === 'REVERT') {
          handleRevertFile(file);
        }
        dialogRef.close();
      },
      onClose: () => {
        dialogRef.close();
        if (type === 'REVERT') {
          showRevertVersion('AIRPORT');
        }
      }
    }
  });
};

const handleRevertFile = async (fileName: string) => {
  isRevert.value = true;
  isLoadingImport.value = true;
  isShowModal.value = true;
  airManagementStore
    .revertAirVersion(fileName)
    .then(() => {
      isSuccess.value = true;
      handleUploadData();
    })
    .catch(() => {
      isSuccess.value = false;
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => {
      isLoadingImport.value = false;
    });
};

const handleCloseModal = (value: boolean) => {
  if (isRevert.value === true) {
    showRevertVersion('AIRPORT');
  }
  isShowModal.value = value;
};

const handleOpenChangeRole = (status) => {
  return (status === STATUS_OPEN && !authorizations?.configurationPage?.air_deactive) || (!authorizations?.configurationPage?.air_active && (status === STATUS_CLOSED || status === null));
};

const filterQuote = useDebounceFn(() => {
  filters.value['global'].value = globalFilterValue.value;
}, 400);

const getAirPermissions = (config: any) => {
  return config?.air_configuration ?? {};
};

handleUploadData();

watch(
  airs,
  (newVal: any) => {
    sheetData.value = [
      {
        name: 'Sheet 1',
        data: formatDataExport(newVal)
      }
    ];
  },
  { deep: true }
);
</script>

<template>
  <LoadingSpinner v-if="isLoadingPage" />
  <div class="card air-management">
    <div class="flex justify-content-between align-items-center">
      <h3 class="h5style">{{ $t('management.air.air_management') }}</h3>
      <div v-if="getAirPermissions(authorizations)?.add_airport">
        <Button type="button" id="add_airport_btn" icon="pi pi-plus" :label="$t('management.air.add_airport')" class="p-button-primary mb-2" @click="editAirport(null, t('management.air.add_airport'))" />
      </div>
    </div>
    <DataTable v-model:filters="filters" :value="airs" paginator showGridlines :loading="loading" :rows="10" dataKey="sk" filterDisplay="menu" :globalFilterFields="['airportCode', 'airportName', 'airportNameFR', 'countryName', 'countryNameFr']">
      <template #header>
        <div class="flex justify-content-between flex-col sm:flex-row">
          <div class="flex justify-content-left gap-3">
            <div>
              <Button id="clear_airport_btn" icon="pi pi-filter-slash" :label="$t('common.buttons.clear')" class="clear-air-btn mb-2" @click="clearFilter()" />
            </div>
            <div v-if="getAirPermissions(authorizations)?.export_airport">
              <ExportExcelFile id="btn-export-excel" class="export-air-btn" :sheets="sheetData" :columnsStyle="sheetColumnStyle" :file-name="renderNameFileExport('Air_Management')"></ExportExcelFile>
            </div>
            <div v-if="getAirPermissions(authorizations)?.import_airport">
              <Button id="btn-import" :label="$t('common.buttons.import')" class="import-air-btn" @click="showModalImportExcel()" />
            </div>
            <div v-if="getAirPermissions(authorizations)?.revert_airport">
              <Button id="btn-revert" :label="$t('common.buttons.revert')" class="revert-air-btn" @click="showRevertVersion('AIRPORT')" />
            </div>
          </div>
          <div class="justify-content-right">
            <span class="p-input-icon-left mb-2">
              <i class="pi pi-search" />
              <InputText id="search_input" v-model="globalFilterValue" @input="filterQuote" :placeholder="$t('my_quote.search_keyword')" class="w-full" />
            </span>
          </div>
        </div>
      </template>
      <template #empty> {{ $t('management.no_data_found') }} </template>
      <template #loading>
        <ProgressSpinner class="w-3rem h-3rem" strokeWidth="8" animationDuration="2s" />
      </template>
      <TableColumn :show-filter-operator="false" :show-add-button="false" field="airportCode" :header="$t('management.air.airport_code')">
        <template #body="{ data }">
          {{ data.airportCode }}
        </template>
        <template #filter="{ filterModel }"> <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.air.search_by_airport_code')" /> </template
      ></TableColumn>
      <TableColumn :show-filter-operator="false" :show-add-button="false" field="airportName" :header="$t('management.air.airport_name')">
        <template #body="{ data }">
          {{ data.airportName }}
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.air.search_by_airport')" />
        </template>
      </TableColumn>
      <TableColumn :show-filter-operator="false" :show-add-button="false" field="airportNameFR" :header="$t('management.air.airport_name_french')">
        <template #body="{ data }">
          {{ data.airportNameFR }}
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.air.search_by_airport')" />
        </template>
      </TableColumn>
      <TableColumn :show-filter-operator="false" :show-add-button="false" field="cityName" :header="$t('management.air.city_name')">
        <template #body="{ data }">
          {{ data.cityName }}
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.air.search_by_city')" />
        </template>
      </TableColumn>
      <TableColumn :show-filter-operator="false" :show-add-button="false" field="cityNameFr" :header="$t('management.air.city_name_french')">
        <template #body="{ data }">
          {{ data.cityNameFr }}
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.air.search_by_city')" />
        </template>
      </TableColumn>
      <TableColumn :show-filter-operator="false" :show-add-button="false" field="region" :header="$t('management.air.region')">
        <template #body="{ data }">
          {{ data.region }}
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.air.search_by_region')" />
        </template>
      </TableColumn>
      <TableColumn :show-filter-operator="false" :show-add-button="false" field="regionFr" :header="$t('management.air.region_french')">
        <template #body="{ data }">
          {{ data.regionFr }}
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.air.search_by_region')" />
        </template>
      </TableColumn>
      <TableColumn :show-filter-operator="false" :show-add-button="false" field="countryName" :header="$t('management.air.country_name')">
        <template #body="{ data }">
          {{ data.countryName }}
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.air.search_by_country')" />
        </template>
      </TableColumn>
      <TableColumn :show-filter-operator="false" :show-add-button="false" field="countryNameFr" :header="$t('management.air.country_name_french')">
        <template #body="{ data }">
          {{ data.countryNameFr }}
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.air.search_by_country')" />
        </template>
      </TableColumn>
      <TableColumn :show-filter-operator="false" :show-add-button="false" field="destSts" :header="$t('management.air.destination_status')">
        <template #body="{ data }">
          <ToggleSwitch v-model="data.destSts" :disabled="handleOpenChangeRole(data.destSts)" :trueValue="STATUS_OPEN" :falseValue="STATUS_CLOSED" @change="onUpdateStatus(data, 'destSts')" />
        </template>
        <template #filter="{ filterModel }">
          <Select v-model="filterModel.value" :options="statuses" :placeholder="$t('common.select_one')" class="p-column-filter" showClear>
            <template #option="slotProps">
              <Tag :value="slotProps.option" :severity="getSeverity(slotProps.option)" />
            </template>
          </Select>
        </template>
      </TableColumn>
      <TableColumn :show-filter-operator="false" :show-add-button="false" field="gateSts" :header="$t('management.air.gateway_status')">
        <template #body="{ data }">
          <ToggleSwitch v-model="data.gateSts" :disabled="handleOpenChangeRole(data.gateSts)" :trueValue="STATUS_OPEN" :falseValue="STATUS_CLOSED" @change="onUpdateStatus(data, 'gateSts')" />
        </template>
        <template #filter="{ filterModel }">
          <Select v-model="filterModel.value" :options="statuses" :placeholder="$t('common.select_one')" class="p-column-filter" showClear>
            <template #option="slotProps">
              <Tag :value="slotProps.option" :severity="getSeverity(slotProps.option)" />
            </template>
          </Select>
        </template>
      </TableColumn>
      <TableColumn :show-filter-operator="false" :show-add-button="false" field="destSVId" :header="$t('management.air.dest_sv_id')">
        <template #body="{ data }">
          {{ data.destSVId }}
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.air.dest_sv_id')" />
        </template>
      </TableColumn>
      <TableColumn :header="$t('common.action')">
        <template #body="{ data }">
          <div class="flex">
            <Button v-if="getAirPermissions(authorizations)?.edit_airport" id="edit_btn" icon="pi pi-pencil" class="p-button-text" @click="editAirport(data, t('management.air.update_air_management'))" />
            <Button v-if="getAirPermissions(authorizations)?.delete_airport" id="delete_btn" icon="pi pi-trash" class="p-button-text" @click="deleteAirport(data)" />
          </div>
        </template>
      </TableColumn>
    </DataTable>
    <Dialog v-model:visible="isShowModal" :header="$t('common.labels.uploading_air')" modal :closable="!isLoadingImport">
      <ProgressModal :isProgress="isLoadingImport" @handleClose="handleCloseModal" :isSuccess="isSuccess" :isRevert="isRevert" />
    </Dialog>
  </div>
</template>
<style lang="scss" scoped>
.export-air {
  :deep() {
    .p-button {
      background-color: #00af87;
      .p-button-icon {
        display: none;
      }
    }
  }
}

.import-button {
  background-color: #0c08e9;
}

.import-button:enabled:hover {
  background-color: #0c08e9;
}
</style>
